import { AUReforestationProject, UKReforestationProject, USReforestationProject } from '../tree-projects';
import { ClimateStatus } from '../user';
import { b2cImpactStats, b2bImpactStats } from './impact-stats';
import { Location } from './countries';
import {
  boosterPlanIdGB,
  boosterPlanIdUS,
  businessPlanIdAU,
  businessPlanIdCA,
  businessPlanIdEU,
  businessPlanIdGB,
  businessPlanIdUS,
  businessPlanIdUSDMedium,
  familyPlanIdAU,
  familyPlanIdCA,
  familyPlanIdEU,
  familyPlanIdGB,
  familyPlanIdUS,
  familyPlanIdUSMedium,
  isCurrentBusinessPlan,
  isCurrentFamilyPlan,
  megaPlanIdGB,
  megaPlanIdUS,
  personalPlanIdAU,
  personalPlanIdGB,
  personalPlanIdUS,
} from './plans';
import { transformLocationIntoValidPlanCountryCode } from './country-transform';

export interface Addons {
  [key: string]: Addon;
}

export interface Addon {
  id: string;
  price: number;
  stats: AddonStats;
  status?: AddonStatus;
  title: string;
  description?: string;
}

export interface AddonStats {
  carbon: number;
  carbonAnnual?: number;
  trees: number;
  footprints?: number;
}

export type AddonStatus = 'friendly' | 'positive';

const ADULTS_TITLE = 'Adults';
const CHILDREN_TITLE = 'Children';
const PETS_TITLE = 'Pets';

export const familyAddonsUS: Addons = {
  adults: {
    id: 'adult-v3-usd',
    title: ADULTS_TITLE,
    price: 10.5,
    stats: b2cImpactStats.high.personal,
  },
  children: {
    id: 'child-v3-usd',
    title: CHILDREN_TITLE,
    price: 5.3,
    stats: b2cImpactStats.high.children,
  },
  pets: {
    id: 'pet-v3-usd',
    title: PETS_TITLE,
    price: 0.7,
    stats: b2cImpactStats.high.pets,
  },
};

export const familyAddonsUSDMedium: Addons = {
  adults: {
    id: 'adult-v3-usd-medium',
    title: ADULTS_TITLE,
    price: 6.4,
    stats: b2cImpactStats.medium.personal,
  },
  children: {
    id: 'child-v3-usd-medium',
    title: CHILDREN_TITLE,
    price: 3.2,
    stats: b2cImpactStats.medium.children,
  },
  pets: {
    id: 'pet-v3-usd-medium',
    title: PETS_TITLE,
    price: 0.7,
    stats: b2cImpactStats.medium.pets,
  },
};

export const familyAddonsAU: Addons = {
  adults: {
    id: 'adult-v3-aud',
    title: ADULTS_TITLE,
    price: 15.4,
    stats: b2cImpactStats.high.personal,
  },
  children: {
    id: 'child-v3-aud',
    title: CHILDREN_TITLE,
    price: 7.7,
    stats: b2cImpactStats.high.children,
  },
  pets: {
    id: 'pet-v3-aud',
    title: PETS_TITLE,
    price: 1,
    stats: b2cImpactStats.high.pets,
  },
};

export const familyAddonsCA: Addons = {
  adults: {
    id: 'adult-v3-cad',
    title: ADULTS_TITLE,
    price: 14.1,
    stats: b2cImpactStats.high.personal,
  },
  children: {
    id: 'child-v3-cad',
    title: CHILDREN_TITLE,
    price: 7.05,
    stats: b2cImpactStats.high.children,
  },
  pets: {
    id: 'pet-v3-cad',
    title: PETS_TITLE,
    price: 1,
    stats: b2cImpactStats.high.pets,
  },
};

export const familyAddonsGB: Addons = {
  adults: {
    id: 'adult-v3-gbp',
    title: ADULTS_TITLE,
    price: 4.7,
    stats: b2cImpactStats.medium.personal,
  },
  children: {
    id: 'child-v3-gbp',
    title: CHILDREN_TITLE,
    price: 2.4,
    stats: b2cImpactStats.medium.children,
  },
  pets: {
    id: 'pet-v3-gbp',
    title: PETS_TITLE,
    price: 0.5,
    stats: b2cImpactStats.medium.pets,
  },
};

export const familyAddonsEU: Addons = {
  adults: {
    id: 'adult-v3-eur',
    title: ADULTS_TITLE,
    price: 5.6,
    stats: b2cImpactStats.medium.personal,
  },
  children: {
    id: 'child-v3-eur',
    title: CHILDREN_TITLE,
    price: 2.8,
    stats: b2cImpactStats.medium.children,
  },
  pets: {
    id: 'pet-v3-eur',
    title: PETS_TITLE,
    price: 0.6,
    stats: b2cImpactStats.medium.pets,
  },
};

export const familyLocationAddonMapper = {
  GB: familyAddonsGB,
  US: familyAddonsUS,
  AU: familyAddonsAU,
  CA: familyAddonsCA,
  EU: familyAddonsEU,
  AD: familyAddonsUSDMedium,
  AF: familyAddonsUSDMedium,
  AG: familyAddonsUSDMedium,
  AL: familyAddonsUSDMedium,
  AM: familyAddonsUSDMedium,
  AO: familyAddonsUSDMedium,
  AQ: familyAddonsUSDMedium,
  AR: familyAddonsUSDMedium,
  AW: familyAddonsUSDMedium,
  AZ: familyAddonsUSDMedium,
  BA: familyAddonsUSDMedium,
  BB: familyAddonsUSDMedium,
  BD: familyAddonsUSDMedium,
  BF: familyAddonsUSDMedium,
  BI: familyAddonsUSDMedium,
  BJ: familyAddonsUSDMedium,
  BM: familyAddonsUSDMedium,
  BO: familyAddonsUSDMedium,
  BQ: familyAddonsUSDMedium,
  BR: familyAddonsUSDMedium,
  BS: familyAddonsUSDMedium,
  BT: familyAddonsUSDMedium,
  BY: familyAddonsUSDMedium,
  CD: familyAddonsUSDMedium,
  CI: familyAddonsUSDMedium,
  CL: familyAddonsUSDMedium,
  CM: familyAddonsUSDMedium,
  CN: familyAddonsUSDMedium,
  CO: familyAddonsUSDMedium,
  CR: familyAddonsUSDMedium,
  CU: familyAddonsUSDMedium,
  CV: familyAddonsUSDMedium,
  CW: familyAddonsUSDMedium,
  DO: familyAddonsUSDMedium,
  DZ: familyAddonsUSDMedium,
  EC: familyAddonsUSDMedium,
  EG: familyAddonsUSDMedium,
  EH: familyAddonsUSDMedium,
  ET: familyAddonsUSDMedium,
  FJ: familyAddonsUSDMedium,
  FK: familyAddonsUSDMedium,
  FM: familyAddonsUSDMedium,
  FO: familyAddonsUSDMedium,
  GA: familyAddonsUSDMedium,
  GD: familyAddonsUSDMedium,
  GE: familyAddonsUSDMedium,
  GG: familyAddonsUSDMedium,
  GH: familyAddonsUSDMedium,
  GI: familyAddonsUSDMedium,
  GM: familyAddonsUSDMedium,
  GP: familyAddonsUSDMedium,
  GQ: familyAddonsUSDMedium,
  GR: familyAddonsUSDMedium,
  GT: familyAddonsUSDMedium,
  GU: familyAddonsUSDMedium,
  HK: familyAddonsUSDMedium,
  HN: familyAddonsUSDMedium,
  HR: familyAddonsUSDMedium,
  HT: familyAddonsUSDMedium,
  ID: familyAddonsUSDMedium,
  IL: familyAddonsUSDMedium,
  IN: familyAddonsUSDMedium,
  IQ: familyAddonsUSDMedium,
  IR: familyAddonsUSDMedium,
  IS: familyAddonsUSDMedium,
  JE: familyAddonsUSDMedium,
  JM: familyAddonsUSDMedium,
  JO: familyAddonsUSDMedium,
  JP: familyAddonsUSDMedium,
  KE: familyAddonsUSDMedium,
  KG: familyAddonsUSDMedium,
  KH: familyAddonsUSDMedium,
  KN: familyAddonsUSDMedium,
  KR: familyAddonsUSDMedium,
  KY: familyAddonsUSDMedium,
  LA: familyAddonsUSDMedium,
  LB: familyAddonsUSDMedium,
  LC: familyAddonsUSDMedium,
  LI: familyAddonsUSDMedium,
  LK: familyAddonsUSDMedium,
  LS: familyAddonsUSDMedium,
  LT: familyAddonsUSDMedium,
  LV: familyAddonsUSDMedium,
  LY: familyAddonsUSDMedium,
  MA: familyAddonsUSDMedium,
  MC: familyAddonsUSDMedium,
  MD: familyAddonsUSDMedium,
  ME: familyAddonsUSDMedium,
  MG: familyAddonsUSDMedium,
  MK: familyAddonsUSDMedium,
  ML: familyAddonsUSDMedium,
  MM: familyAddonsUSDMedium,
  MN: familyAddonsUSDMedium,
  MO: familyAddonsUSDMedium,
  MQ: familyAddonsUSDMedium,
  MR: familyAddonsUSDMedium,
  MT: familyAddonsUSDMedium,
  MU: familyAddonsUSDMedium,
  MV: familyAddonsUSDMedium,
  MW: familyAddonsUSDMedium,
  MX: familyAddonsUSDMedium,
  MY: familyAddonsUSDMedium,
  MZ: familyAddonsUSDMedium,
  NA: familyAddonsUSDMedium,
  NC: familyAddonsUSDMedium,
  NE: familyAddonsUSDMedium,
  NF: familyAddonsUSDMedium,
  NG: familyAddonsUSDMedium,
  NI: familyAddonsUSDMedium,
  NP: familyAddonsUSDMedium,
  NZ: familyAddonsUSDMedium,
  PA: familyAddonsUSDMedium,
  PE: familyAddonsUSDMedium,
  PF: familyAddonsUSDMedium,
  PG: familyAddonsUSDMedium,
  PH: familyAddonsUSDMedium,
  PK: familyAddonsUSDMedium,
  PR: familyAddonsUSDMedium,
  PS: familyAddonsUSDMedium,
  PY: familyAddonsUSDMedium,
  RE: familyAddonsUSDMedium,
  RS: familyAddonsUSDMedium,
  RU: familyAddonsUSDMedium,
  RW: familyAddonsUSDMedium,
  SC: familyAddonsUSDMedium,
  SD: familyAddonsUSDMedium,
  SG: familyAddonsUSDMedium,
  SI: familyAddonsUSDMedium,
  SK: familyAddonsUSDMedium,
  SL: familyAddonsUSDMedium,
  SM: familyAddonsUSDMedium,
  SN: familyAddonsUSDMedium,
  SO: familyAddonsUSDMedium,
  SS: familyAddonsUSDMedium,
  SV: familyAddonsUSDMedium,
  SY: familyAddonsUSDMedium,
  SZ: familyAddonsUSDMedium,
  TD: familyAddonsUSDMedium,
  TG: familyAddonsUSDMedium,
  TH: familyAddonsUSDMedium,
  TJ: familyAddonsUSDMedium,
  TL: familyAddonsUSDMedium,
  TN: familyAddonsUSDMedium,
  TR: familyAddonsUSDMedium,
  TV: familyAddonsUSDMedium,
  TW: familyAddonsUSDMedium,
  TZ: familyAddonsUSDMedium,
  UA: familyAddonsUSDMedium,
  UG: familyAddonsUSDMedium,
  UY: familyAddonsUSDMedium,
  UZ: familyAddonsUSDMedium,
  VC: familyAddonsUSDMedium,
  VE: familyAddonsUSDMedium,
  VG: familyAddonsUSDMedium,
  VI: familyAddonsUSDMedium,
  VN: familyAddonsUSDMedium,
  VU: familyAddonsUSDMedium,
  XK: familyAddonsUSDMedium,
  YE: familyAddonsUSDMedium,
  YT: familyAddonsUSDMedium,
  ZA: familyAddonsUSDMedium,
  ZM: familyAddonsUSDMedium,
  ZW: familyAddonsUSDMedium,
  AE: familyAddonsUS,
  BH: familyAddonsUS,
  BN: familyAddonsUS,
  BW: familyAddonsUS,
  BZ: familyAddonsUS,
  EE: familyAddonsUS,
  GY: familyAddonsUS,
  KW: familyAddonsUS,
  KZ: familyAddonsUS,
  LU: familyAddonsUS,
  OM: familyAddonsUS,
  QA: familyAddonsUS,
  SA: familyAddonsUS,
  SR: familyAddonsUS,
  TM: familyAddonsUS,
  TT: familyAddonsUS,
  AT: familyAddonsEU,
  BE: familyAddonsEU,
  DK: familyAddonsEU,
  FI: familyAddonsEU,
  FR: familyAddonsEU,
  DE: familyAddonsEU,
  IT: familyAddonsEU,
  IE: familyAddonsEU,
  NL: familyAddonsEU,
  NO: familyAddonsEU,
  PL: familyAddonsEU,
  ES: familyAddonsEU,
  SE: familyAddonsEU,
  CH: familyAddonsEU,
  CZ: familyAddonsEU,
  HU: familyAddonsEU,
  CY: familyAddonsEU,
  PT: familyAddonsEU,
  RO: familyAddonsEU,
  BG: familyAddonsEU,
} as { [location: string]: Addons };

export const familyAddons = [
  familyAddonsGB,
  familyAddonsUS,
  familyAddonsAU,
  familyAddonsCA,
  familyAddonsEU,
  familyAddonsUSDMedium,
].reduce((allFamilyAddons, addonSetForCurrency) => {
  Object.values(addonSetForCurrency).forEach(addon => {
    allFamilyAddons[addon.id] = addon;
  });

  return allFamilyAddons;
}, {});

const tier1 = {
  title: 'Starter plan',
  description: 'Get started in your climate action journey.',
  status: ClimateStatus.FRIENDLY,
};

const tier2 = {
  title: 'Essential plan',
  description: 'Make an impact with our most popular plan.',
  status: ClimateStatus.POSITIVE,
};

const tier3 = {
  title: 'Ultimate plan',
  description: 'Double your impact and put the climate at the top of your agenda.',
  status: ClimateStatus.POSITIVE,
};

export const businessAddonsUS: Addons = {
  tier1: {
    id: 'tier-1-v3-usd',
    price: 16.25,
    stats: b2bImpactStats.high.tier1,
    ...tier1,
  },
  tier2: {
    id: 'tier-2-v3-usd',
    price: 32.5,
    stats: b2bImpactStats.high.tier2,
    ...tier2,
  },
  tier3: {
    id: 'tier-3-v3-usd',
    price: 64.95,
    stats: b2bImpactStats.high.tier3,
    ...tier3,
  },
};

export const businessAddonsUSDMedium: Addons = {
  tier1: {
    id: 'tier-1-v3-usd-medium',
    price: 7.65,
    stats: b2bImpactStats.medium.tier1,
    ...tier1,
  },
  tier2: {
    id: 'tier-2-v3-usd-medium',
    price: 15.3,
    stats: b2bImpactStats.medium.tier2,
    ...tier2,
  },
  tier3: {
    id: 'tier-3-v3-usd-medium',
    price: 30.6,
    stats: b2bImpactStats.medium.tier3,
    ...tier3,
  },
};

export const businessAddonsAU: Addons = {
  tier1: {
    id: 'tier-1-v3-aud',
    price: 21.8,
    stats: b2bImpactStats.high.tier1,
    ...tier1,
  },
  tier2: {
    id: 'tier-2-v3-aud',
    price: 43.6,
    stats: b2bImpactStats.high.tier2,
    ...tier2,
  },
  tier3: {
    id: 'tier-3-v3-aud',
    price: 87.2,
    stats: b2bImpactStats.high.tier3,
    ...tier3,
  },
};

export const businessAddonsCA: Addons = {
  tier1: {
    id: 'tier-1-v3-cad',
    price: 20.3,
    stats: b2bImpactStats.high.tier1,
    ...tier1,
  },
  tier2: {
    id: 'tier-2-v3-cad',
    price: 40.6,
    stats: b2bImpactStats.high.tier2,
    ...tier2,
  },
  tier3: {
    id: 'tier-3-v3-cad',
    price: 81.2,
    stats: b2bImpactStats.high.tier3,
    ...tier3,
  },
};

export const businessAddonsGB: Addons = {
  tier1: {
    id: 'tier-1-v3-gbp',
    price: 5.35,
    stats: b2bImpactStats.medium.tier1,
    ...tier1,
  },
  tier2: {
    id: 'tier-2-v3-gbp',
    price: 10.7,
    stats: b2bImpactStats.medium.tier2,
    ...tier2,
  },
  tier3: {
    id: 'tier-3-v3-gbp',
    price: 21.4,
    stats: b2bImpactStats.medium.tier3,
    ...tier3,
  },
};

export const businessAddonsEU: Addons = {
  tier1: {
    id: 'tier-1-v3-eur',
    price: 6.45,
    stats: b2bImpactStats.medium.tier1,
    ...tier1,
  },
  tier2: {
    id: 'tier-2-v3-eur',
    price: 12.9,
    stats: b2bImpactStats.medium.tier2,
    ...tier2,
  },
  tier3: {
    id: 'tier-3-v3-eur',
    price: 25.8,
    stats: b2bImpactStats.medium.tier3,
    ...tier3,
  },
};

export const businessAddons = [
  businessAddonsGB,
  businessAddonsUS,
  businessAddonsAU,
  businessAddonsCA,
  businessAddonsEU,
  businessAddonsUSDMedium,
].reduce((allBusinessAddons, addonSetForCurrency) => {
  Object.values(addonSetForCurrency).forEach(addon => {
    allBusinessAddons[addon.id] = addon;
  });

  return allBusinessAddons;
}, {});

export const businessLocationAddonMapper = {
  GB: businessAddonsGB,
  US: businessAddonsUS,
  AU: businessAddonsAU,
  CA: businessAddonsCA,
  EU: businessAddonsEU,
  AD: businessAddonsUSDMedium,
  AF: businessAddonsUSDMedium,
  AG: businessAddonsUSDMedium,
  AL: businessAddonsUSDMedium,
  AM: businessAddonsUSDMedium,
  AO: businessAddonsUSDMedium,
  AQ: businessAddonsUSDMedium,
  AR: businessAddonsUSDMedium,
  AW: businessAddonsUSDMedium,
  AZ: businessAddonsUSDMedium,
  BA: businessAddonsUSDMedium,
  BB: businessAddonsUSDMedium,
  BD: businessAddonsUSDMedium,
  BF: businessAddonsUSDMedium,
  BI: businessAddonsUSDMedium,
  BJ: businessAddonsUSDMedium,
  BM: businessAddonsUSDMedium,
  BO: businessAddonsUSDMedium,
  BQ: businessAddonsUSDMedium,
  BR: businessAddonsUSDMedium,
  BS: businessAddonsUSDMedium,
  BT: businessAddonsUSDMedium,
  BY: businessAddonsUSDMedium,
  CD: businessAddonsUSDMedium,
  CI: businessAddonsUSDMedium,
  CL: businessAddonsUSDMedium,
  CM: businessAddonsUSDMedium,
  CN: businessAddonsUSDMedium,
  CO: businessAddonsUSDMedium,
  CR: businessAddonsUSDMedium,
  CU: businessAddonsUSDMedium,
  CV: businessAddonsUSDMedium,
  CW: businessAddonsUSDMedium,
  DO: businessAddonsUSDMedium,
  DZ: businessAddonsUSDMedium,
  EC: businessAddonsUSDMedium,
  EG: businessAddonsUSDMedium,
  EH: businessAddonsUSDMedium,
  ET: businessAddonsUSDMedium,
  FJ: businessAddonsUSDMedium,
  FK: businessAddonsUSDMedium,
  FM: businessAddonsUSDMedium,
  FO: businessAddonsUSDMedium,
  GA: businessAddonsUSDMedium,
  GD: businessAddonsUSDMedium,
  GE: businessAddonsUSDMedium,
  GG: businessAddonsUSDMedium,
  GH: businessAddonsUSDMedium,
  GI: businessAddonsUSDMedium,
  GM: businessAddonsUSDMedium,
  GP: businessAddonsUSDMedium,
  GQ: businessAddonsUSDMedium,
  GR: businessAddonsUSDMedium,
  GT: businessAddonsUSDMedium,
  GU: businessAddonsUSDMedium,
  HK: businessAddonsUSDMedium,
  HN: businessAddonsUSDMedium,
  HR: businessAddonsUSDMedium,
  HT: businessAddonsUSDMedium,
  ID: businessAddonsUSDMedium,
  IL: businessAddonsUSDMedium,
  IN: businessAddonsUSDMedium,
  IQ: businessAddonsUSDMedium,
  IR: businessAddonsUSDMedium,
  IS: businessAddonsUSDMedium,
  JE: businessAddonsUSDMedium,
  JM: businessAddonsUSDMedium,
  JO: businessAddonsUSDMedium,
  JP: businessAddonsUSDMedium,
  KE: businessAddonsUSDMedium,
  KG: businessAddonsUSDMedium,
  KH: businessAddonsUSDMedium,
  KN: businessAddonsUSDMedium,
  KR: businessAddonsUSDMedium,
  KY: businessAddonsUSDMedium,
  LA: businessAddonsUSDMedium,
  LB: businessAddonsUSDMedium,
  LC: businessAddonsUSDMedium,
  LI: businessAddonsUSDMedium,
  LK: businessAddonsUSDMedium,
  LS: businessAddonsUSDMedium,
  LT: businessAddonsUSDMedium,
  LV: businessAddonsUSDMedium,
  LY: businessAddonsUSDMedium,
  MA: businessAddonsUSDMedium,
  MC: businessAddonsUSDMedium,
  MD: businessAddonsUSDMedium,
  ME: businessAddonsUSDMedium,
  MG: businessAddonsUSDMedium,
  MK: businessAddonsUSDMedium,
  ML: businessAddonsUSDMedium,
  MM: businessAddonsUSDMedium,
  MN: businessAddonsUSDMedium,
  MO: businessAddonsUSDMedium,
  MQ: businessAddonsUSDMedium,
  MR: businessAddonsUSDMedium,
  MT: businessAddonsUSDMedium,
  MU: businessAddonsUSDMedium,
  MV: businessAddonsUSDMedium,
  MW: businessAddonsUSDMedium,
  MX: businessAddonsUSDMedium,
  MY: businessAddonsUSDMedium,
  MZ: businessAddonsUSDMedium,
  NA: businessAddonsUSDMedium,
  NC: businessAddonsUSDMedium,
  NE: businessAddonsUSDMedium,
  NF: businessAddonsUSDMedium,
  NG: businessAddonsUSDMedium,
  NI: businessAddonsUSDMedium,
  NP: businessAddonsUSDMedium,
  NZ: businessAddonsUSDMedium,
  PA: businessAddonsUSDMedium,
  PE: businessAddonsUSDMedium,
  PF: businessAddonsUSDMedium,
  PG: businessAddonsUSDMedium,
  PH: businessAddonsUSDMedium,
  PK: businessAddonsUSDMedium,
  PR: businessAddonsUSDMedium,
  PS: businessAddonsUSDMedium,
  PY: businessAddonsUSDMedium,
  RE: businessAddonsUSDMedium,
  RS: businessAddonsUSDMedium,
  RU: businessAddonsUSDMedium,
  RW: businessAddonsUSDMedium,
  SC: businessAddonsUSDMedium,
  SD: businessAddonsUSDMedium,
  SG: businessAddonsUSDMedium,
  SI: businessAddonsUSDMedium,
  SK: businessAddonsUSDMedium,
  SL: businessAddonsUSDMedium,
  SM: businessAddonsUSDMedium,
  SN: businessAddonsUSDMedium,
  SO: businessAddonsUSDMedium,
  SS: businessAddonsUSDMedium,
  SV: businessAddonsUSDMedium,
  SY: businessAddonsUSDMedium,
  SZ: businessAddonsUSDMedium,
  TD: businessAddonsUSDMedium,
  TG: businessAddonsUSDMedium,
  TH: businessAddonsUSDMedium,
  TJ: businessAddonsUSDMedium,
  TL: businessAddonsUSDMedium,
  TN: businessAddonsUSDMedium,
  TR: businessAddonsUSDMedium,
  TV: businessAddonsUSDMedium,
  TW: businessAddonsUSDMedium,
  TZ: businessAddonsUSDMedium,
  UA: businessAddonsUSDMedium,
  UG: businessAddonsUSDMedium,
  UY: businessAddonsUSDMedium,
  UZ: businessAddonsUSDMedium,
  VC: businessAddonsUSDMedium,
  VE: businessAddonsUSDMedium,
  VG: businessAddonsUSDMedium,
  VI: businessAddonsUSDMedium,
  VN: businessAddonsUSDMedium,
  VU: businessAddonsUSDMedium,
  XK: businessAddonsUSDMedium,
  YE: businessAddonsUSDMedium,
  YT: businessAddonsUSDMedium,
  ZA: businessAddonsUSDMedium,
  ZM: businessAddonsUSDMedium,
  ZW: businessAddonsUSDMedium,
  AE: businessAddonsUS,
  BH: businessAddonsUS,
  BN: businessAddonsUS,
  BW: businessAddonsUS,
  BZ: businessAddonsUS,
  EE: businessAddonsUS,
  GY: businessAddonsUS,
  KW: businessAddonsUS,
  KZ: businessAddonsUS,
  LU: businessAddonsUS,
  OM: businessAddonsUS,
  QA: businessAddonsUS,
  SA: businessAddonsUS,
  SR: businessAddonsUS,
  TM: businessAddonsUS,
  TT: businessAddonsUS,
  AT: businessAddonsEU,
  BE: businessAddonsEU,
  DK: businessAddonsEU,
  FI: businessAddonsEU,
  FR: businessAddonsEU,
  DE: businessAddonsEU,
  IT: businessAddonsEU,
  IE: businessAddonsEU,
  NL: businessAddonsEU,
  NO: businessAddonsEU,
  PL: businessAddonsEU,
  ES: businessAddonsEU,
  SE: businessAddonsEU,
  CH: businessAddonsEU,
  CZ: businessAddonsEU,
  HU: businessAddonsEU,
  CY: businessAddonsEU,
  PT: businessAddonsEU,
  BG: businessAddonsEU,
  RO: businessAddonsEU,
} as { [location: string]: Addons };

// Helper for building arrays of addons
const addonBuilder = (name: string) => [
  `${name}-v3-gbp`,
  `${name}-v3-aud`,
  `${name}-v3-cad`,
  `${name}-v3-eur`,
  `${name}-v3-usd`,
  `${name}-v3-usd-medium`,
];

export const tier1AddonIds = addonBuilder('tier-1');
export const tier2AddonIds = addonBuilder('tier-2');
export const tier3AddonIds = addonBuilder('tier-3');
export const adultAddonIds = addonBuilder('adult');
export const childAddonIds = addonBuilder('child');
export const petAddonIds = addonBuilder('pet');

/**
 * Helper method which returns the identifiers of those addons which are valid (and required) for the currently offered plans
 * Note - this data should be moved to a database along with the rest of the plan data
 * @param {string} planId
 * @returns {string[]}
 */
export const getRequiredAddonsForPlanId = (planId: string) => {
  switch (planId) {
    case businessPlanIdGB:
      return ['tier-1-v3-gbp', 'tier-2-v3-gbp', 'tier-3-v3-gbp'];

    case businessPlanIdUS:
      return ['tier-1-v3-usd', 'tier-2-v3-usd', 'tier-3-v3-usd'];

    case businessPlanIdUSDMedium:
      return ['tier-1-v3-usd-medium', 'tier-2-v3-usd-medium', 'tier-3-v3-usd-medium'];

    case businessPlanIdEU:
      return ['tier-1-v3-eur', 'tier-2-v3-eur', 'tier-3-v3-eur'];

    case businessPlanIdAU:
      return ['tier-1-v3-aud', 'tier-2-v3-aud', 'tier-3-v3-aud'];

    case businessPlanIdCA:
      return ['tier-1-v3-cad', 'tier-2-v3-cad', 'tier-3-v3-cad'];

    case familyPlanIdGB:
      return ['adult-v3-gbp', 'child-v3-gbp', 'pet-v3-gbp'];

    case familyPlanIdUS:
      return ['adult-v3-usd', 'child-v3-usd', 'pet-v3-usd'];

    case familyPlanIdUSMedium:
      return ['adult-v3-usd-medium', 'child-v3-usd-medium', 'pet-v3-usd-medium'];

    case familyPlanIdEU:
      return ['adult-v3-eur', 'child-v3-eur', 'pet-v3-eur'];

    case familyPlanIdAU:
      return ['adult-v3-aud', 'child-v3-aud', 'pet-v3-aud'];

    case familyPlanIdCA:
      return ['adult-v3-cad', 'child-v3-cad', 'pet-v3-cad'];

    default:
      return [];
  }
};

/**
 */
export const getOptionalAddonsForPlanId = (planId: string) => {
  switch (planId) {
    case personalPlanIdGB:
    case boosterPlanIdGB:
    case megaPlanIdGB:
      return [UKReforestationProject.id];

    case personalPlanIdUS:
    case boosterPlanIdUS:
    case megaPlanIdUS:
      return [USReforestationProject.id];

    case personalPlanIdAU:
      return [AUReforestationProject.id];

    case businessPlanIdGB:
      return [UKReforestationProject.id];

    case businessPlanIdUS:
      return [USReforestationProject.id];

    case businessPlanIdAU:
      return [AUReforestationProject.id];

    case familyPlanIdGB:
      return [UKReforestationProject.id];

    case familyPlanIdUS:
      return [USReforestationProject.id];

    case familyPlanIdAU:
      return [AUReforestationProject.id];

    default:
      return [];
  }
};

/**
 * Is there at least one required addon supplied for the planId
 */
export const hasMinimumRequiredAddonsForPlan = (planId: string, addonIds: string[]): boolean => {
  const isBusinessPlan = isCurrentBusinessPlan(planId);
  const isFamilyPlan = isCurrentFamilyPlan(planId);

  //  Is the planId for one which has required addons
  if (isBusinessPlan || isFamilyPlan) {
    const requiredPlanAddons = getRequiredAddonsForPlanId(planId);
    const suppliedAddonsSet = new Set(addonIds);

    //  Ensure first of all that if the plan requires add-ons that at least one of those is supplied otherwise it is an error
    const requiredIntersectionAddonsSet = new Set([...requiredPlanAddons].filter(a => suppliedAddonsSet.has(a)));

    if (isBusinessPlan) {
      return requiredIntersectionAddonsSet.size > 0;
    } else {
      //  You must have 2 or more family addons for it to be considered a valid subscription)
      //  So (1 Adult & 1 Child) or (1 Adult & 1 Pet) or (1 Child & 1 Pet) or (1 Adult & 1 Child & 1 Pet)
      return requiredIntersectionAddonsSet.size > 1;
    }
  }

  return true;
};

/**
 * Helper method to determine if the specified addons are valid for the given planId
 * @param {string} planId - The identifier of the subscription plan
 * @param {string[]} addonIds - The array of addon identifiers to test
 * @param {Location} location - The location in which the check for addons against the plan is being made
 */
export const areAllValidAddonsForPlan = (planId: string, addonIds: string[], location: Location | null): boolean => {
  const requiredPlanAddons = getRequiredAddonsForPlanId(planId);
  const requiredPlanAddonsSet = new Set<string>(requiredPlanAddons);
  const optionalPlanAddons = getOptionalAddonsForPlanId(planId);
  const suppliedAddonsSet = new Set(addonIds);

  const countryCode = transformLocationIntoValidPlanCountryCode(location);

  //  We need to remove the USReforestation project Id from Saudi Arabia and United Arab Emirates
  if (countryCode === 'SA' || countryCode === 'AE') {
    const USReforestationIdx = optionalPlanAddons.indexOf(USReforestationProject.id);
    optionalPlanAddons.splice(USReforestationIdx, 1);
  }

  const optionalPlanAddonsSet = new Set<string>(optionalPlanAddons);

  if (hasMinimumRequiredAddonsForPlan(planId, addonIds)) {
    // Perform a Set Union to get all valid plan addon details
    const allPlanAddonIds = new Set([...requiredPlanAddonsSet, ...optionalPlanAddonsSet]);

    //  Perform a Set Intersection to determine if all of the supplied addonIds are in the list of
    //  only those addons which are valid for the Plan
    const intersectionAddons = new Set([...allPlanAddonIds].filter(a => suppliedAddonsSet.has(a)));

    //  If the number of addons supplied is the same as the number of addons which are allowed for the plan
    //  then we have a valid set of supplied addons (otherwise there is an error)
    return intersectionAddons.size === suppliedAddonsSet.size;
  } else {
    return false;
  }
};

export const isCurrentAddon = (addonId: string) => {
  return addonId.includes('v3');
};

export const isUSMediumAddon = (addonId: string) => {
  return addonId.includes('medium');
};

export const isBusinessAddon = (addonId: string) => {
  return addonId.includes('tier');
};

export const isTierOneAddon = (addonId: string) => {
  return addonId.includes('tier-1');
};

export const isTierTwoAddon = (addonId: string) => {
  return addonId.includes('tier-2');
};

export const isTierThreeAddon = (addonId: string) => {
  return addonId.includes('tier-3');
};

export const isFamilyAddon = (addonId: string) => {
  return isAdultAddon(addonId) || isChildAddon(addonId) || isPetAddon(addonId);
};

export const isAdultAddon = (addonId: string) => {
  return addonId.includes('adult');
};

export const isChildAddon = (addonId: string) => {
  return addonId.includes('child');
};

export const isPetAddon = (addonId: string) => {
  return addonId.includes('pet');
};
